import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CardContent,
  CardHeader
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import GenderPieChart from 'src/scenes/statistics/StatResultByGender/GenderPieChart';
import LastComplaintsTable from './lastComplaint';
import { modeDescriptions } from 'src/constants/competitions';
import { StyledCard } from './helper';

/* POCETNA STRANA ADMINA */

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [recentComplaints, setRecentComplaints] = useState([]);
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [totalCountStudent, setTotalCountStudent] = useState(0);
  const [totalCountSchool, setTotalCountSchool] = useState(0);
  const [totalCountComplaint, setTotalCountComplaint] = useState(0);
  const [studentByClass, setStudentByClass] = useState(0);

  const { activeModes } = useContext(ActiveModesContext);

  const activeModeKey = activeModes.find((mode) => modeDescriptions[mode]);

  const activeMode = activeModeKey
    ? t(`modes.${activeModeKey}`, { defaultValue: activeModeKey })
    : t('noActiveMode');

  const generateTicks = (maxValue) => {
    const ticks = [];
    let step = 1;

    if (maxValue > 1000) {
      step = Math.pow(10, Math.floor(Math.log10(maxValue)));
    } else if (maxValue > 100) {
      step = 100;
    } else if (maxValue > 10) {
      step = 10;
    }

    for (let i = step; i <= maxValue; i += step) {
      ticks.push(i);
    }

    // Uvek uključite maksimum ako nije pokriven
    if (ticks[ticks.length - 1] !== maxValue) {
      ticks.push(maxValue);
    }

    return ticks;
  };
  // Izračunajte maksimalnu vrednost kada se podaci promene
  const maxCount =
    studentByClass.length > 0
      ? Math.max(...studentByClass.map((item) => item.count))
      : 5300; // Default vrednost ako nema podataka
  const yAxisTicks = generateTicks(maxCount);

  useEffect(() => {
    // // Fetch broj studenata
    axios
      .get(`${API_BASE_URL}/api/students.php?student_count`)
      .then((response) => setTotalCountStudent(response.data.count))
      .catch((error) => console.error('Error fetching student count:', error));

    // // Fetch broj škola
    axios
      .get(`${API_BASE_URL}/api/schools.php?totalCount`)
      .then((response) => setTotalCountSchool(response.data.totalCount))
      .catch((error) => console.error('Error fetching school count:', error));

    // // Fetch broj žalbi
    axios
      .get(`${API_BASE_URL}/api/complaints.php?complaint_count_no_response`)
      .then((response) => setTotalCountComplaint(response.data))
      .catch((error) =>
        console.error('Error fetching complaint count:', error)
      );

    axios
      .get(
        `${API_BASE_URL}/api/statistics.php?statistics=student_count_by_class`
      )
      .then((response) => {
        setStudentByClass(response.data);
      })
      .catch((error) =>
        console.error('Error fetching complaint count:', error)
      );
    // statistika za pol
    axios
      .get(
        `${API_BASE_URL}/api/statistics.php?statistics=student_count_by_gender`
      )
      .then((response) => setGenderDistribution(response.data))
      .catch((error) =>
        console.error('Error fetching complaint count:', error)
      );

    // last 5 complaints
    axios
      .get(`${API_BASE_URL}/api/complaints.php?complaint_no_response_last`)
      .then((response) => setRecentComplaints(response.data))
      .catch((error) =>
        console.error('Error fetching complaint count:', error)
      );
  }, []);

  return (
    <Box m="20px">
      <Typography variant="h4" gutterBottom>
        {t('adminDashboard.title')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardHeader title={t('adminDashboard.students')} />
            <CardContent>
              <Typography variant="h6">
                {t('adminDashboard.total_students')}
              </Typography>
              <Typography variant="h4">
                {totalCountStudent ? totalCountStudent : 0}
              </Typography>
              <Button
                variant="contained"
                sx={{ marginTop: '10px', backgroundColor: '#1e88e5' }}
                href="#/manageStudent"
              >
                {t('adminDashboard.manage_students')}
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardHeader title={t('adminDashboard.schools')} />
            <CardContent>
              <Typography variant="h6">
                {t('adminDashboard.total_schools')}
              </Typography>
              <Typography variant="h4">
                {totalCountSchool ? totalCountSchool : 0}
              </Typography>
              <Button
                variant="contained"
                sx={{ marginTop: '10px', backgroundColor: '#26a69a' }}
                href="#/schools"
              >
                {t('adminDashboard.manage_schools')}
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardHeader title={t('adminDashboard.complaints')} />
            <CardContent>
              <Typography variant="h6">
                {t('adminDashboard.pending_complaints')}
              </Typography>
              <Typography variant="h4">
                {totalCountComplaint ? totalCountComplaint : 0}
              </Typography>
              <Button
                variant="contained"
                sx={{ marginTop: '10px', backgroundColor: '#546e7a' }}
                href="#/complaints-admin-page"
              >
                {t('adminDashboard.manage_complaints')}
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardHeader title={t('adminDashboard.phase_competition')} />
            <CardContent>
              <Typography variant="h6">
                {t('adminDashboard.current_mode')}
              </Typography>
              <Typography variant="h4">
                {activeModes ?? activeModes.length > 0
                  ? activeMode
                  : t('adminDashboard.no_active_mode')}
              </Typography>
              <Button
                variant="contained"
                sx={{ marginTop: '10px', backgroundColor: '#fdd835' }}
                href="#/competition-modes"
              >
                {t('adminDashboard.manage_competition')}
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ marginTop: '20px' }}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
              height: '400px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: '#2596be', fontWeight: 'bold' }}
            >
              {t('adminDashboard.students_by_class')}
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={studentByClass}>
                <XAxis
                  dataKey="class"
                  tick={{ fill: '#2596be', fontWeight: 'bold' }}
                  label={{
                    position: 'insideBottomRight',
                    offset: -10,
                    fill: '#2596be',
                    fontWeight: 'bold'
                  }}
                />
                <YAxis
                  tick={{ fill: '#2596be', fontWeight: 'bold' }}
                  label={{
                    angle: -90,
                    position: 'insideLeft',
                    fill: '#2596be',
                    fontWeight: 'bold'
                  }}
                  domain={[0, maxCount * 1.1]}
                  scale="linear"
                  ticks={yAxisTicks}
                />
                <Tooltip
                  formatter={(value) => [`${t('headers.total')}: ${value}`]}
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #cccccc',
                    borderRadius: '8px'
                  }}
                  cursor={{ fill: 'rgba(37, 150, 190, 0.1)' }}
                />
                <Bar
                  dataKey="count"
                  fill={'#2596be'}
                  barSize={40}
                  radius={[10, 10, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              height: '400px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',

              alignItems: 'center'
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <GenderPieChart
                data={genderDistribution}
                showTitle={1}
                colors={['#A6CEE3', '#86A6B7']}
                valueKey="count"
              />
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ marginTop: '20px' }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.6)'
            }}
          >
            <Typography variant="h6" gutterBottom>
              {t('adminDashboard.recent_complaints')}
            </Typography>
            <LastComplaintsTable complaints={recentComplaints} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
